import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo, useRef, useEffect, useState } from "react";
import _ from "lodash";
import {
  handleAddRdm,
  handleRemoveRdm,
  handlePaginate,
} from "../../../state/rdms/actions";
import { getRdmState } from "../../../state/rdms/selectors";
import { toastAlert } from "../../../components/Toastify";

export function useFetch({
  titleSearch,
  typeFilter,
  setTypeFilter,
  setTitleSearch,
  setUrlParams,
}) {
  const dispatch = useDispatch();
  const isSearchChanging = useRef(false);

  const { data, loading: globalLoading, totalPages } = useSelector(getRdmState);

  const [loading, setLoading] = useState(false);

  const refetch = useCallback(
    (pagination) => {
      dispatch(handlePaginate(pagination));
    },
    [dispatch]
  );

  const debouncedFetch = useMemo(
    () =>
      _.debounce((pagination) => {
        refetch(pagination);
      }, 1500),
    [refetch]
  );

  const handleDebouncedSearchTitle = useCallback(
    (value) => {
      const noLeadingSpaces = value.replace(/^ +/, "");
      const trimmed = noLeadingSpaces.trim();
      if (noLeadingSpaces === titleSearch) return;
      if (trimmed.length === 0) {
        debouncedFetch.cancel();
        setTitleSearch("");
        setTypeFilter((prev) => ({ ...prev, page: 1 }));
        setUrlParams({ title: "" });
        setLoading(false);
        return;
      }
      if (trimmed.length < 3) {
        debouncedFetch.cancel();
        setTitleSearch(noLeadingSpaces);
        setTypeFilter((prev) => ({ ...prev, page: 1 }));
        setUrlParams({ title: noLeadingSpaces });
        setLoading(false);
        return;
      }
      isSearchChanging.current = true;
      setTypeFilter((prev) => ({ ...prev, page: 1 }));
      setTitleSearch(noLeadingSpaces);
      setUrlParams({ title: noLeadingSpaces });
      setLoading(true);
      debouncedFetch({
        page: 1,
        limit: 3,
        sortOrder: -1,
        title: trimmed,
      });
    },
    [setTypeFilter, setTitleSearch, setUrlParams, debouncedFetch, titleSearch]
  );

  useEffect(() => {
    if (isSearchChanging.current) {
      isSearchChanging.current = false;
      setLoading(false);
      return;
    }
    const trimmed = titleSearch.trim();
    if (trimmed.length === 0 || trimmed.length >= 3) {
      dispatch(
        handlePaginate({
          page: typeFilter.page,
          limit: 3,
          sortOrder: typeFilter.creation ? 1 : -1,
          title: trimmed,
        })
      );
      setLoading(false);
    }
  }, [dispatch, typeFilter.creation, typeFilter.page, titleSearch]);

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const onCreate = useCallback(
    async (rdmData, pagination = { page: 1, limit: 3 }) => {
      try {
        await handleAddRdm(rdmData);
        refetch(pagination);
        toastAlert({
          msg: `Rdm CRIADO com sucesso!`,
        });
      } catch (error) {
        console.log(error);
        toastAlert({
          msg: `Ocorreu um erro ao criar o RDM.`,
          isError: true,
        });
      }
    },
    [refetch]
  );

  const [actualRdm, setActualRdm] = useState();
  const [openRemoveModal, setOpenRemoveModal] = useState(false);

  const onDelete = useCallback(async () => {
    try {
      setOpenRemoveModal(false);
      await handleRemoveRdm(actualRdm._id);
      refetch({ page: typeFilter.page, limit: 3 });
      toastAlert({
        msg: `Rdm REMOVIDO com sucesso!`,
      });
    } catch (error) {
      setOpenRemoveModal(false);
      toastAlert({
        msg: `Ocorreu um Erro ao remover o Rdm.`,
        isError: true,
      });
      console.log(error);
    }
  }, [actualRdm, typeFilter.page, refetch]);

  return {
    data,
    loading: loading || globalLoading,
    totalPages,
    refetch,
    handleDebouncedSearchTitle,
    onCreate,
    onDelete,
    actualRdm,
    setActualRdm,
    openRemoveModal,
    setOpenRemoveModal,
  };
}
